import React, { useEffect, useRef, useState } from 'react';
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, FormControl, FormLabel, Input, Button, Grid, Select, Heading, IconButton, Box, Image, VStack } from '@chakra-ui/react';
import { X, Upload } from 'lucide-react';
import type { DepartmentType, RoleOption, UserType } from '@/types';
import { CreateUserFormData, UpdateUserFormData } from '@/lib/validations/auth';
import { userService } from '@/lib/services/userService';
import apiClient from '@/lib/api/client';

interface UserModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (payload: Record<string, any>) => Promise<void>;
    editingUser?: UserType | null;
}

const UserModal: React.FC<UserModalProps> = ({ isOpen, onClose, onSave, editingUser = null }) => {
    const initialRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [roles, setRoles] = useState<RoleOption[]>([]);
    const [departmentChoices, setDepartmentChoies] = useState<DepartmentType[]>([]);
    const [isLoadingRoles, setIsLoadingRoles] = useState(true);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const [department, setDepartment] = useState('');
    const [roleId, setRoleId] = useState('');
    const [status, setStatus] = useState<number>(1);

    const [imageId, setImageId] = useState<number | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await apiClient.get('/options/roles');
                const rolesData = Array.isArray(response.data) ? response.data : response.data.data || [];
                const filteredRoles = rolesData.filter((role: RoleOption) => role.name !== 'Developer Account');
                setRoles(filteredRoles);
            } catch (error: any) {
                console.error(error);
            } finally {
                setIsLoadingRoles(false);
            }
        };
        fetchRoles();
        const fetchDepartment = async () => {
            setDepartmentChoies(await userService.getDepartment());
        };

        fetchRoles();
        fetchDepartment();
    }, []);

    useEffect(() => {
        console.log(editingUser);
        if (isOpen && editingUser) {
            setFirstName(editingUser.first_name || '');
            setLastName(editingUser.last_name || '');
            setPhoneNumber(editingUser.phone_number || '');
            setEmail(editingUser.user_email || '');

            if (editingUser.department && departmentChoices.length > 0) {
                const matchedDepartment = departmentChoices.find((dt) => dt.department === editingUser.department);

                if (matchedDepartment) {
                    setDepartment(matchedDepartment.id.toString());
                }
            }

            if (typeof editingUser.user_role === 'object' && editingUser.user_role?.id) {
                setRoleId(editingUser.user_role.id.toString());
            } else if (editingUser.user_role) {
                setRoleId(editingUser.user_role.toString());
            }

            if (typeof editingUser.user_status === 'string') {
                setStatus(editingUser.user_status === 'Inactive' ? 0 : 1);
            } else if (typeof editingUser.user_status === 'number') {
                setStatus(editingUser.user_status);
            }

            setImageId(editingUser.image_id ?? null);
            setImagePreview(editingUser.image?.file_path || null);
        }

        if (!isOpen) {
            setFirstName('');
            setLastName('');
            setPhoneNumber('');
            setEmail('');
            setDepartment('');
            setRoleId('');
            setStatus(1);
            setImageId(null);
            setImagePreview(null);
            setSelectedFile(null);
        }
    }, [isOpen, editingUser]);

    const handleSave = async () => {
        if (!firstName || !lastName || !phoneNumber || !email || !department || !roleId) return;

        const selectedRole = roles.find((r) => r.id.toString() === roleId);
        if (!selectedRole) return;

        // Generate user_login from first and last name
        const userLogin = `${firstName.toLowerCase()}.${lastName.toLowerCase()}`.replace(/\s+/g, '');

        const payload: Record<string, any> = {
            first_name: firstName,
            last_name: lastName,
            phone_number: phoneNumber,
            user_email: email,
            user_login: userLogin,
            department,
            position: department,
            employee_id: '',
            user_role: { id: selectedRole.id },
            user_status: status,
        };

        if (editingUser?.id) {
            payload.id = editingUser.id;
            if (selectedFile) payload.image = selectedFile;
            else if (imageId) payload.image_id = imageId;
        } else {
            if (selectedFile) payload.image = selectedFile;
        }

        await onSave(payload);
        onClose();
    };

    const isDisabled = !firstName || !lastName || !phoneNumber || !email || !department || !roleId;

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size='lg' initialFocusRef={initialRef} >
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(1px)"  />
            <ModalContent borderRadius={0}>
                <ModalHeader>
                    {editingUser ? 'Edit User' : 'New User'}
                    <IconButton aria-label='Close' icon={<X />} onClick={onClose} position='absolute' right={4} top={4} variant='ghost' />
                </ModalHeader>

                <ModalBody>
                    <div ref={initialRef} tabIndex={-1} />
                    <Heading size='sm' mb={4}>
                        User Information
                    </Heading>

                    <Grid templateColumns='repeat(2, 1fr)' gap={4}>
                        <FormControl isRequired>
                            <FormLabel>First Name</FormLabel>
                            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>Last Name</FormLabel>
                            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>Phone Number</FormLabel>
                            <Input value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>Email Address</FormLabel>
                            <Input type='email' value={email} onChange={(e) => setEmail(e.target.value)} />
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>Department</FormLabel>
                            <Select value={department} onChange={(e) => setDepartment(e.target.value)} placeholder={isLoadingRoles ? 'Loading departments...' : 'Select department'} isDisabled={isLoadingRoles}>
                                {departmentChoices.map((dt: DepartmentType) => (
                                    <option key={dt.id} value={dt.id}>
                                        {dt.department}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>Role</FormLabel>
                            <Select value={roleId} onChange={(e) => setRoleId(e.target.value)} placeholder={isLoadingRoles ? 'Loading roles...' : 'Select role'} isDisabled={isLoadingRoles}>
                                {roles
                                    .filter((role) => !['Doctor', 'Patient'].includes(role.name))
                                    .map((role) => (
                                        <option key={role.id} value={role.id.toString()}>
                                            {role.name}
                                        </option>
                                    ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <FormControl mt={4} isRequired>
                        <FormLabel>Status</FormLabel>
                        <Select value={status} onChange={(e) => setStatus(Number(e.target.value))}>
                            <option value={1}>Active</option>
                            <option value={0}>Inactive</option>
                        </Select>
                    </FormControl>
                </ModalBody>

                <ModalFooter>
                    <Button variant='ghost' onClick={onClose}>
                        Cancel
                    </Button>
                    <Button colorScheme='red' onClick={handleSave} isDisabled={isDisabled}>
                        {editingUser ? 'Save' : 'Create User'}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default UserModal;
